import type { AppResources } from './pt-BR'

export const enUS: AppResources = {
  common: {
    productName: 'Codout.Support',
    loading: 'Loading...',
    retry: 'Try again',
    cancel: 'Cancel',
    error: 'This step could not be completed.',
    errorReference: 'Error reference',
    toast: {
      dismiss: 'Dismiss',
      variant: {
        success: 'Success',
        error: 'Error',
        info: 'Info',
        warning: 'Warning',
      },
    },
    appError: {
      title: 'Something went wrong',
      description: 'We hit a problem loading this screen. Please try again in a moment.',
      retry: 'Try again',
      home: 'Go to home',
    },
    notFound: {
      title: 'Page not found',
      description: 'The address you tried to open does not exist or has moved.',
      home: 'Go to home',
    },
    auth: {
      login: {
        title: 'Sign in',
        subtitle: 'Access your account to continue.',
        emailLabel: 'Email',
        passwordLabel: 'Password',
        submit: 'Sign in',
        submitting: 'Signing in...',
        forgotLink: 'Forgot my password',
        signupPrompt: "Don't have an account yet?",
        signupLink: 'Create organization',
        errors: {
          emailRequired: 'Enter a valid email.',
          passwordRequired: 'Enter your password.',
        },
      },
      forgot: {
        title: 'Recover password',
        subtitle: 'Enter your email and we will send a link to create a new password.',
        emailLabel: 'Email',
        submit: 'Send link',
        submitting: 'Sending...',
        backToLogin: 'Back to login',
        sentTitle: 'Check your email',
        sentDescription:
          'If an account exists for that email, we sent a link to reset the password. The link expires in a few hours.',
        errors: {
          emailRequired: 'Enter a valid email.',
        },
      },
      reset: {
        title: 'Create a new password',
        subtitle: 'Choose a new password for your account.',
        passwordLabel: 'New password',
        passwordHint: 'At least 12 characters, with uppercase, lowercase and a number.',
        confirmPasswordLabel: 'Confirm new password',
        submit: 'Reset password',
        submitting: 'Resetting...',
        invalidLinkTitle: 'Invalid or expired link',
        invalidLinkDescription:
          'This reset link is not valid or has expired. Request a new one to continue.',
        requestNewLink: 'Request a new link',
        successTitle: 'Password reset',
        successDescription:
          'Your password has been updated. You can now sign in with the new password.',
        goToLogin: 'Go to login',
        errors: {
          passwordLength: 'The password must be at least 12 characters.',
          passwordMismatch: 'The passwords do not match.',
        },
      },
      signup: {
        title: 'Create account',
        subtitle: 'Takes less than a minute.',
        nameLabel: 'Name',
        emailLabel: 'Email',
        passwordLabel: 'Password',
        passwordHint: 'At least 12 characters.',
        confirmPasswordLabel: 'Confirm password',
        submit: 'Create account',
        submitting: 'Creating account...',
        loginPrompt: 'Already have an account?',
        loginLink: 'Sign in',
        errors: {
          nameRequired: 'Enter your name.',
          emailRequired: 'Enter a valid email.',
          passwordLength: 'Password must be at least 12 characters long.',
          passwordMismatch: 'Passwords do not match.',
        },
      },
      userMenu: {
        openLabel: 'Open user menu',
        signedInAs: 'Signed in as',
        logout: 'Sign out',
        loggingOut: 'Signing out...',
      },
    },
    onboarding: {
      createOrganization: {
        eyebrow: 'Step 1',
        heading: 'About your company',
        subtitle:
          'Your organization brings together team, agents, channels and conversations in one place.',
        nameLabel: 'Organization name',
        websiteLabel: 'Company website',
        websiteHint: 'Optional. We will use it to help your agent learn about your business.',
        websitePlaceholder: 'mycompany.com',
        submit: 'Create organization',
        submitting: 'Creating...',
        errors: {
          nameLength: 'Enter the organization name (minimum 2 characters).',
        },
      },
      wizard: {
        title: 'Set up your support',
        stepOf: 'Step {{current}} of {{total}}',
        stepperLabel: 'Setup steps',
        steps: {
          organization: 'Organization',
          playground: 'Test',
          connect: 'WhatsApp',
          goLive: 'Ready',
        },
      },
      playground: {
        eyebrow: 'Try it',
        title: 'Chat with your agent',
        description:
          'Ask a question as if you were your customer. The agent answers here, without sending anything to anyone.',
        agentModeLabel: 'Mode',
        agentModeValue: 'Observe only',
        loadingAgent: 'Preparing your agent...',
        emptyTitle: 'Start the conversation',
        emptyHint: 'Type a question or try one of these:',
        suggestions: {
          first: 'What payment methods do you accept?',
          second: 'What is the delivery time?',
          third: 'How do I exchange a product?',
        },
        inputLabel: 'Your message',
        inputPlaceholder: 'Type as if you were your customer...',
        send: 'Send',
        thinking: 'The agent is thinking...',
        handoffNote:
          'Your agent would hand this conversation to a human teammate, with the history ready.',
        firstValueTitle: 'Done! Your agent just replied.',
        firstValueDescription:
          'This is how it will help your team. Keep testing or put it to work.',
        providerUnavailableTitle: 'The agent is not available yet',
        providerUnavailableDescription:
          "We're preparing your agent's intelligence. Try again in a moment.",
        errorTitle: 'Could not get the reply',
        errorDescription: 'Try again in a moment.',
        disclaimer: 'This is a test. No message is sent to your customers.',
        youLabel: 'You',
        agentLabel: 'Agent',
        continuePrompt: 'Like what you see?',
        continueCta: "Let's put it to work",
      },
      connect: {
        eyebrow: 'Step 3',
        title: 'Connect WhatsApp',
        description:
          "Let's set up your support and connect the number that talks to your customers.",
        startDescription:
          'When you are ready, connect WhatsApp so your agent starts following real conversations.',
        startCta: 'Connect WhatsApp',
        instructions1: 'Open WhatsApp on the phone that serves your customers.',
        instructions2: 'Tap Linked devices.',
        instructions3: 'Point the camera at the code on the side.',
        dedicatedNumberNotice:
          'Use a number dedicated to support. Your personal number stays separate.',
        errorDetails: 'Code {{code}} · reference {{correlationId}}',
        progress: {
          title: 'Setting up your support',
          milestones: {
            channel: 'Creating your WhatsApp channel',
            pairing: 'Connecting WhatsApp',
            agent: 'Preparing your AI agent',
            finish: 'Finishing setup',
          },
        },
        qr: {
          preparingTitle: 'Preparing the connection code',
          preparingDescription: 'This takes just a moment.',
          imageAlt: 'QR code to connect WhatsApp',
          expiresIn: 'This code expires in {{time}}',
          refresh: 'Generate new code',
          expiredTitle: 'Code expired',
          expiredDescription:
            'No one scanned the code in time. Generate a new one and keep WhatsApp open on the phone.',
          pairingCodeLabel: 'Or enter the code',
          connectedTitle: 'WhatsApp connected',
          connectedDescription: 'Your number is connected. Finishing setup...',
          unavailableTitle: 'Preparing the WhatsApp connection',
          unavailableDescription:
            'We are preparing your WhatsApp channel. This should take just a moment.',
          errorTitle: 'Could not load the code',
          errorDescription: 'Try again in a moment.',
        },
      },
      goLive: {
        title: 'Your support is live',
        description:
          'From now on your agent follows the WhatsApp conversations and prepares replies for your team to approve.',
        modeLabel: 'Starting mode',
        modeName: 'Observe only',
        modeDescription:
          'No message is sent automatically. You can turn on suggestions and automatic replies whenever you want, in the agent settings.',
        primaryCta: 'Go to overview',
        comingSoon: {
          title: 'What comes next',
          reason: 'These steps arrive once AI setup is available.',
          badge: 'Coming soon',
          discovery: {
            title: 'Automatic website discovery',
            description: 'Your agent learns on its own from your website pages.',
          },
          instantAgent: {
            title: 'Guided agent',
            description: 'Adjust role, tone and hours in minutes.',
          },
        },
      },
    },
    knowledge: {
      pageTitle: 'Knowledge',
      pageSubtitle: 'Teach your agent to answer from your own content.',
      discovery: {
        title: 'Teach your agent',
        subtitle: 'Your agent learns on its own from your website pages.',
        add: {
          label: 'Learn from a URL',
          placeholder: 'https://mycompany.com/help',
          cta: 'Learn from this URL',
          invalidUrl: 'Enter a URL that starts with http:// or https://.',
        },
        empty: {
          title: 'Import your website',
          description:
            'Add a URL above or learn from the website you registered so the agent can start answering from it.',
          cta: 'Import my website',
          learnFromSite: 'Learn from the registered website',
        },
        sources: {
          title: 'Added sources',
          registeredSite: 'Registered website',
          failed: 'We could not learn from this source. Check the URL and try again.',
        },
        source: {
          remove: 'Remove the source {{url}}',
          confirmQuestion:
            'Remove this source? The agent stops re-learning from it. The pages already learned remain until you delete them under Learned pages.',
          confirm: 'Yes, remove',
          cancel: 'Cancel',
          error: 'Could not remove the source. Try again.',
        },
        websiteMissing: {
          title: 'Add your company website first',
          description: 'Add your company website so the agent can learn from it.',
          label: 'Company website',
          placeholder: 'mycompany.com',
          cta: 'Add website and learn',
          invalid: 'Enter a valid website (e.g. mycompany.com).',
        },
        progress: {
          title: 'Learning from your website',
          learned: '{{pages}} pages learned',
          discovered: '{{pages}} pages found',
        },
        status: {
          queued: 'Queued',
          crawling: 'Reading your website',
          indexing: 'Learning the content',
          ready: 'Ready',
          failed: 'Could not finish',
        },
        ready: {
          title: 'Your agent learned from the website',
          description: '{{pages}} pages from your website now help the agent answer.',
          relearn: 'Learn again',
        },
        failed: {
          title: 'We could not learn from your website',
          description: 'Check that the site is reachable and try again.',
          retry: 'Try again',
        },
        loadError: 'Could not load the learning status.',
        retry: 'Try again',
      },
      documents: {
        title: 'Learned pages',
        empty: 'No pages learned yet.',
        loadError: 'Could not load the learned pages.',
        summary: '{{ready}} of {{total}} ready',
        columns: {
          name: 'Page',
          visibility: 'Visibility',
          status: 'Status',
          learnedAt: 'Learned at',
          actions: 'Actions',
        },
        status: {
          ready: 'Ready',
          processing: 'Processing',
          pending: 'Queued',
          failed: 'Failed',
        },
        visibility: {
          customer: 'Visible',
          internal: 'Internal use',
        },
        delete: {
          action: 'Delete {{name}}',
          confirmQuestion: 'Delete? This removes the document and what the agent learned from it.',
          confirm: 'Yes, delete',
          cancel: 'Cancel',
          error: 'Could not delete the document. Try again.',
        },
      },
      upload: {
        title: 'Upload documents',
        subtitle: 'Beyond your website, the agent also learns from the files you upload here.',
        dropzone: {
          instruction: 'Drag files here or pick from your computer',
          hint: 'TXT, Markdown, PDF, Word and Excel · up to 10 MB per file',
          button: 'Choose files',
          inputLabel: 'Choose files to upload',
        },
        internal: {
          label: 'Internal use',
          hint: 'The agent uses this content but does not repeat it to the customer.',
        },
        list: {
          title: 'Uploaded files',
        },
        status: {
          uploading: 'Uploading',
          processing: 'Processing',
          ready: 'Ready',
          error: 'Failed',
        },
        item: {
          dismiss: 'Remove {{name}} from the list',
        },
        rejected: {
          title: 'Some files were not accepted',
          unsupportedType:
            '{{name}}: unsupported format. Upload TXT, Markdown, PDF, Word or Excel.',
          fileTooLarge: '{{name}}: file is larger than 10 MB.',
          emptyFile: '{{name}}: the file is empty.',
        },
        errors: {
          unsupportedType: 'Unsupported format. Upload TXT, Markdown, PDF, Word or Excel.',
          fileTooLarge: 'File exceeds the maximum size of 10 MB.',
          emptyFile: 'The file is empty.',
          unreadable: 'We could not read the content of this file.',
          contentTooLarge:
            'The document content is too large to process. Try splitting it into smaller files.',
          aiNotConfigured: 'AI processing is not available yet. Contact support.',
          processingFailed: 'We could not process the document. Try uploading it again.',
          processingTimeout:
            'The document is large and processing timed out. Try splitting it into smaller files.',
          uploadTimeout: 'The upload took too long. Check your connection and try again.',
          statusUnavailable: 'We could not check the progress. Refresh the page.',
          generic: 'We could not upload the file. Try again.',
        },
      },
    },
    agents: {
      pageTitle: 'Agents',
      pageSubtitle:
        'See your agents, choose how each one acts, and chat with them before turning anything on.',
      loading: 'Getting your agents ready...',
      loadError: 'Could not load your agents.',
      actionFailed: 'We could not complete the action.',
      empty: {
        title: 'No agents here yet',
        description:
          'Your agent is created during initial setup. If you just got started, refresh in a moment.',
        refresh: 'Refresh',
      },
      card: {
        statusLabel: 'Status',
        testCta: 'Test agent',
        hideTestCta: 'Hide test',
        edit: 'Edit',
        enable: 'Enable',
        disable: 'Disable',
      },
      toast: {
        created: 'Agent "{{name}}" created',
        updated: 'Agent updated',
        enabled: 'Agent enabled',
        disabled: 'Agent disabled',
      },
      create: {
        cta: 'Create agent',
        title: 'Create an agent',
        subtitle:
          'Give it a name and, optionally, describe its persona and instructions. It starts observing only.',
        cancel: 'Cancel',
        submit: 'Create agent',
        submitting: 'Creating...',
        errorTitle: 'We could not create the agent',
      },
      edit: {
        title: 'Edit agent',
        subtitle: "Adjust the agent's name, description and instructions.",
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving...',
      },
      persona: {
        nameLabel: 'Agent name',
        namePlaceholder: 'e.g. Support',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'One line about what this agent does (optional).',
        systemPromptLabel: 'Instructions',
        systemPromptPlaceholder: 'How the agent should behave, tone, limits...',
        systemPromptHint:
          'Instructions guide the replies. An agent without instructions cannot reply on its own (automatic mode).',
        templates: {
          title: 'Start from a template',
          hint: 'Pick the one closest to your business and adjust the text. You can edit everything later.',
          replaceQuestion: 'Replace the current instructions with the “{{name}}” template?',
          replace: 'Replace',
          cancel: 'Keep current',
          fallbackCompany: 'our company',
          items: {
            general: {
              name: 'General support',
              text: 'You are the customer service assistant for {{company}}. Reply politely, clearly and objectively, in short messages (as in a WhatsApp chat).\n\n- Use only the knowledge base and the available lookups. If you do not know or are not sure, do not make things up: say you will check and hand off to a human agent.\n- If the customer asks to talk to a person, hand off without insisting.\n- Ask one question at a time when you need more details.\n- Never ask for passwords, verification codes or full card details.\n- Close by asking whether there is anything else you can help with.',
            },
            techSupport: {
              name: 'Technical support (software)',
              text: 'You are the technical support for {{company}}. Help customers with questions and problems using the system, in plain language without jargon.\n\n- Before answering, understand the problem: ask for the screen or feature, what the customer tried and the exact error message, one question at a time.\n- Give short, numbered steps with menu names exactly as they appear in the system.\n- Use only the knowledge base and the available lookups; do not invent features, deadlines or settings.\n- If it looks like a system bug, an outage or data loss, or if the problem persists after two attempts, hand off to a human agent with a summary of what was tried.\n- Never ask for the customer password.',
            },
            sales: {
              name: 'Sales and pre-sales',
              text: 'You are the sales consultant for {{company}}. Your goal is to understand what the customer needs and show how we can help, without pressure.\n\n- Ask questions to understand the context (business size, what they use today, main pain point), one at a time.\n- Present only plans, prices, terms and deadlines found in the knowledge base; never invent discounts or promises.\n- Highlight the benefits tied to what the customer told you.\n- When the customer wants to buy, asks for a custom proposal or wants to negotiate, hand off to a human agent with a summary of the conversation.\n- Be friendly and objective, with short messages.',
            },
            ecommerce: {
              name: 'Online store (orders and shipping)',
              text: 'You are the customer service for the {{company}} store. Help with questions about products, orders, shipping, exchanges and returns, in short messages.\n\n- To talk about an order, use the available lookups; if you cannot find it, ask for the order number or the email used for the purchase.\n- State deadlines and policies (shipping, exchange, return, warranty) only as described in the knowledge base.\n- Do not promise refunds, chargebacks, gifts or policy exceptions: in those cases, and for defective products or orders not received, hand off to a human agent.\n- Be empathetic about delays and problems, without blaming carriers or third parties.\n- Never ask for full card details.',
            },
            scheduling: {
              name: 'Appointments (clinics and services)',
              text: 'You are the virtual front desk for {{company}}. Be friendly and help with services, hours, address, preparation and appointments, in short messages.\n\n- State services, prices, insurance plans and hours only as described in the knowledge base.\n- To book, reschedule or cancel, collect the full name, the desired service and the preferred day and time, then hand off to a human agent to confirm.\n- Do not give diagnosis or treatment advice; refer the customer to the professional.\n- In urgent or emergency situations, advise seeking immediate care and hand off to a human.\n- Handle personal data discreetly.',
            },
            billing: {
              name: 'Billing and payments',
              text: 'You are the billing support for {{company}}. Help with invoices, payment slips, payment methods and subscription status, respectfully and clearly.\n\n- Use the available lookups for customer information; do not assume amounts, dates or status.\n- Explain how to get a copy of an invoice and the payment methods as described in the knowledge base.\n- Do not negotiate discounts, installments, fee waivers or refunds: hand off to a human agent with a summary of the request.\n- For disputed charges or unhappy customers, hand off to a human.\n- Never ask for passwords, verification codes or full card details, and never send payment links that are not in the knowledge base.',
            },
          },
        },
      },
      statuses: {
        Draft: 'Draft',
        Active: 'Active',
        Disabled: 'Disabled',
      },
      playground: {
        title: 'Test this agent',
        subtitle: 'Chat as if you were your customer. Nothing is sent to your customers.',
      },
      mode: {
        currentLabel: 'Current mode',
        currentBadge: 'Current',
        sectionTitle: 'How this agent acts',
        sectionHint: 'You can switch the mode anytime. The current mode is always visible.',
        toast: {
          changed: 'Mode updated to {{mode}}',
        },
        confirm: {
          title: 'Turn on auto reply?',
          description:
            'In this mode the agent replies to your customers on its own, with no one reviewing first. You can switch back to suggestions anytime.',
          confirmCta: 'Yes, turn on auto reply',
          cancel: 'Cancel',
        },
        errors: {
          prerequisiteTitle: "Auto reply can't be turned on yet",
          tenantNotAllowed: "Your organization isn't enabled for auto reply yet. Contact support.",
          channelNotReady: 'Connect and verify your support channel before turning on auto reply.',
          agentMissingInstructions:
            'This agent has no Instructions yet — they tell it how to answer. Open “Edit agent”, write the instructions and try again. (This is unrelated to your knowledge base material.)',
          agentNotEligible:
            "This agent isn't ready to reply on its own yet. Stay on suggestions for now.",
          prerequisiteGeneric:
            "Some requirements haven't been met yet. Check your channel and try again.",
          confirmationRequired: 'Confirm to turn on auto reply.',
          notFound: 'This agent is no longer available. Refresh the page.',
          generic: 'Could not change the mode. Please try again.',
        },
      },
      modes: {
        observeOnly: {
          label: 'Observing',
          description: 'The agent follows conversations and learns, without replying.',
        },
        suggest: {
          label: 'Suggesting',
          description: 'The agent drafts replies for your team to approve before sending.',
        },
        autoReply: {
          label: 'Auto reply',
          description:
            'The agent replies to your customers on its own. Requires confirmation to enable.',
        },
      },
    },
    channels: {
      pageTitle: 'Channels',
      pageSubtitle: 'See your support channels and each one’s connection status.',
      loading: 'Loading your channels...',
      loadError: 'Could not load your channels.',
      empty: {
        title: 'No channels connected yet',
        description:
          'Your WhatsApp is connected during your organization’s initial setup. If you just got started, refresh in a moment.',
        refresh: 'Refresh',
      },
      status: {
        connected: 'Connected',
        waiting: 'Waiting to connect',
        disconnected: 'Disconnected',
        failed: 'Failed',
      },
      card: {
        typeWhatsApp: 'WhatsApp',
        createdLabel: 'Created on {{date}}',
        updatedLabel: 'Updated on {{date}}',
        reconnect: 'Reconnect',
        regenerate: 'Generate a new code',
        close: 'Close',
        connectedHint: 'Your WhatsApp is connected.',
      },
      reconnect: {
        title: 'Reconnect WhatsApp',
        description:
          'Scan the code with the same WhatsApp that talks to your customers to reconnect this channel.',
      },
    },
    webchat: {
      pageTitle: 'WebChat',
      pageSubtitle:
        'Put an AI chat on your own website. Create a widget, copy the code, and your signed-in users are recognized automatically.',
      loading: 'Loading your widgets...',
      loadError: 'We could not load your widgets.',
      loadErrorHint: 'Refresh the page in a moment.',
      empty: {
        title: 'No widgets yet',
        description: 'Create your first widget above to embed the chat on your website.',
      },
      create: {
        title: 'Create a widget',
        subtitle: 'Give it a name and list the websites (origins) where it may appear.',
        nameLabel: 'Widget name',
        namePlaceholder: 'e.g. Main website',
        originsLabel: 'Allowed origins',
        originsHint: 'One per line (e.g. https://app.yourcustomer.com). You can change this later.',
        submit: 'Create widget',
        submitting: 'Creating...',
        errorTitle: 'We could not create the widget',
        errorGeneric: 'Please try again in a moment.',
      },
      card: {
        active: 'Active',
        disabled: 'Disabled',
        enable: 'Enable',
        disable: 'Disable',
        originsLabel: 'Allowed origins',
        editOrigins: 'Edit',
        saveOrigins: 'Save origins',
        cancel: 'Cancel',
        noOrigins: 'No origins configured — the widget will not appear on any website yet.',
        rotate: 'Generate new secret',
        rotating: 'Generating...',
        rotateHint: 'Generates a new signing secret and invalidates the previous one.',
        actionFailedTitle: 'We could not complete the action',
        actionFailedGeneric: 'Please try again in a moment.',
      },
      toast: {
        created: 'Widget created',
        enabled: 'Widget enabled',
        disabled: 'Widget disabled',
        originsSaved: 'Origins updated',
      },
      credentials: {
        title: 'Widget credentials',
        subtitle: 'Copy and store now — the signing secret will not be shown again.',
        done: 'Done',
        secretWarningTitle: 'Store the signing secret securely',
        secretWarning:
          'The signing secret lives only in your backend, which signs the identity tokens of your signed-in users. Never expose it in the browser.',
        publishableKey: 'Publishable key (public)',
        signingSecret: 'Signing secret (shown once)',
        snippet: 'Embed code',
        snippetTokenPlaceholder: '<token signed by your backend>',
        copy: 'Copy',
        copied: 'Copied!',
        copyFailed: 'Failed — select and copy',
        tokenNote:
          'The signed token is generated by your backend with the signing secret, for each signed-in user. See the token issuance docs.',
      },
    },
    integrations: {
      pageTitle: 'Integrations',
      pageSubtitle: 'See the business systems your agent can query live while it helps customers.',
      loading: 'Loading your integrations...',
      loadError: 'Could not load your integrations.',
      intro: {
        title: 'Data that changes, answered on the spot',
        description:
          'To answer things that change — like orders and plans — the agent queries your business systems live. Queries are read-only and run securely.',
      },
      health: {
        healthy: 'Connected',
        unavailable: 'Unavailable',
      },
      card: {
        toolsHint: 'Your agent can query this live when replying to a customer:',
        toolsLabel: 'Live queries available',
        noTools: 'No queries available in this connector yet.',
      },
      riskLevels: {
        readOnly: 'Read',
        lowRiskWrite: 'Write',
        highRiskWrite: 'Sensitive write',
        destructive: 'Destructive',
      },
      empty: {
        title: 'No integrations available yet',
        description:
          'Integrations with your business systems will appear here. When available, your agent can query live data — like orders and plans — to answer your customers.',
        refresh: 'Refresh',
        roadmapNote:
          'Connecting your own systems (by importing your API) is part of the product roadmap.',
      },
    },
    billing: {
      title: 'Plans and usage',
      subtitle: 'Track your trial, your AI interaction balance and your organization usage.',
      interactions: 'interactions',
      plans: {
        title: 'Plans',
        subtitle:
          'Choose the right plan. Payment happens on a secure page; no card is stored here.',
        perMonth: '/month',
        priceUnset: 'Coming soon',
        priceUnsetHint: 'This plan price is still being defined.',
        current: 'Current plan',
        awaitingPayment: 'Awaiting payment',
        subscribe: 'Subscribe',
        changeTo: 'Switch to this plan',
        scheduled: 'Starting next renewal',
        upgradeCreated:
          'Invoice for the difference created. Once paid, the new plan applies immediately.',
        downgradeScheduled: 'Change scheduled for the next renewal.',
        subscribing: 'Creating invoice…',
        contactSales: 'Talk to sales',
        onlyOwner: 'Only the owner can subscribe or cancel.',
        gatewayUnavailable: 'Payment is unavailable right now. Please try again later.',
        names: {
          starter: 'Starter',
          professional: 'Professional',
          enterprise: 'Enterprise',
        },
        limits: {
          interactions: '{{value}} AI interactions per month',
          channels: '{{value}} channel(s)',
          agents: '{{value}} agent(s)',
          users: '{{value}} team member(s)',
          storage: '{{value}} MB of knowledge',
          triageOn: 'Triage included',
          triageOff: 'No Triage',
          custom: 'Custom limits',
        },
      },
      profile: {
        title: 'Billing details',
        description: 'Used to issue invoices. The document is always shown masked.',
        legalName: 'Name or company name',
        taxId: 'CPF or CNPJ',
        email: 'Billing email',
        save: 'Save details',
        saveAndSubscribe: 'Save and subscribe',
        saved: 'Billing details saved.',
        empty: 'No billing details yet.',
        edit: 'Edit',
      },
      invoice: {
        title: 'Open invoice',
        number: 'Invoice {{number}}',
        due: 'Due on {{date}}',
        payNow: 'Pay now',
        issuing: 'We are creating the payment link. It shows up here in a moment.',
        pixTitle: 'Pix copy and paste',
        copy: 'Copy Pix code',
        copied: 'Pix code copied.',
        awaitingActivation: 'Your plan activates automatically after payment.',
      },
      packs: {
        title: 'Extra interactions',
        subtitle:
          'Buy one-off AI interactions. Valid for {{months}} months and used after the monthly allowance.',
        requiresPlan: 'Extra interactions are available with an active plan.',
        credits: '{{value}} interactions',
        buy: 'Buy',
        ordered: 'Invoice created. Interactions are added once the payment is confirmed.',
      },
      invoices: {
        title: 'Invoices',
        empty: 'No invoices yet',
        emptyDescription: 'When you subscribe to a plan, invoices show up here.',
        columns: {
          number: 'Number',
          date: 'Issued on',
          amount: 'Amount',
          status: 'Status',
          action: 'Action',
        },
        statuses: {
          Issuing: 'Creating',
          Issued: 'Open',
          Paid: 'Paid',
          Overdue: 'Overdue',
          Canceled: 'Canceled',
          Refunded: 'Refunded',
        },
        pay: 'Pay',
      },
      subscription: {
        statuses: {
          PendingFirstPayment: 'Awaiting payment',
          Active: 'Active',
          PastDue: 'Payment pending',
          Canceled: 'Canceled',
        },
        cancelScheduled: 'Cancellation scheduled for {{date}}.',
      },
      cancel: {
        title: 'Cancel plan',
        description:
          'The plan stays valid until the end of the paid period and paid interactions remain available until then.',
        action: 'Cancel plan',
        confirmTitle: 'Cancel the plan?',
        confirmDescription:
          'Your plan is valid until the end of the paid period. After that, the agent pauses when interactions run out.',
        confirm: 'Yes, cancel',
        keep: 'Keep plan',
        done: 'Cancellation recorded.',
      },
      errors: {
        plan_unavailable: 'This plan is not available for subscription.',
        billing_profile_required: 'Add your billing details before subscribing.',
        invalid_tax_id: 'Invalid CPF or CNPJ.',
        validation_error: 'Check the details you entered.',
        subscription_already_active: 'You already have an active subscription.',
        subscription_not_found: 'No active subscription.',
        payment_gateway_not_configured: 'Payment is unavailable right now.',
        payment_gateway_error: 'Could not reach the payment service. Please try again.',
        generic: 'Could not complete. Please try again.',
        credit_pack_unavailable: 'This pack is not available.',
        credit_pack_requires_subscription: 'Extra interactions require an active plan.',
        plan_limit_reached: 'You reached your plan limit. Choose a larger plan to continue.',
        plan_required: 'Choose a plan to continue.',
      },
      states: {
        Trial: 'Free trial',
        TrialEnded: 'Trial ended',
        Active: 'Active plan',
        PastDue: 'Payment pending',
        Canceled: 'Plan canceled',
      },
      current: {
        title: 'Current status',
        available: 'Available interactions',
        trialEndsAt: 'Trial until {{date}}',
        periodEnd: 'Period until {{date}}',
        plansSoon: 'Plan selection is coming here soon. Until then, your support keeps running.',
        loadError: 'Could not load your organization status.',
      },
      usage: {
        title: 'Usage this period',
        windowLabel: 'Period',
        window: 'Last {{days}} days',
        loadError: 'Could not load usage.',
        buckets: {
          title: 'Balance by source',
          empty: 'No active balance right now.',
          consumedOf: '{{consumed}} of {{credits}} used',
          expiresAt: 'Valid until {{date}}',
          noExpiry: 'No expiry',
          kinds: {
            Trial: 'Free trial',
            PlanAllowance: 'Plan allowance',
            TopUp: 'Extra interactions',
            Adjustment: 'Courtesy',
          },
        },
        byKind: {
          title: 'Usage by type',
          kinds: {
            AgentExecution: 'Agent replies',
            PlaygroundExecution: 'Playground',
            TriageRun: 'Triage',
            AudioTranscription: 'Audio transcription',
          },
        },
        daily: {
          title: 'Daily usage',
          ariaLabel: 'AI interactions used per day',
          series: 'Interactions',
        },
        empty: {
          title: 'No interactions used in this period',
          description: 'Try the agent in the Playground to see usage here.',
          action: 'Open Agents',
        },
      },
      noPermission: {
        title: 'Usage details restricted',
        description: 'Only the owner and administrators can see usage details.',
      },
      banner: {
        paused:
          'The agent is paused: your AI interactions ran out. Messages keep arriving and your team can reply.',
        trialEnded: 'Your trial has ended. Choose a plan to keep the agent running.',
        pastDue: 'A payment is pending. Settle it to keep your plan.',
        openInvoice: 'Your invoice is available. Your plan is activated after payment.',
        lowBalance: '{{available}} interactions left in this period.',
        trial: 'Free trial: {{available}} interactions until {{date}}. No credit card.',
        actions: {
          viewPlans: 'View plans and usage',
          viewUsage: 'View usage',
          payNow: 'Pay now',
          dismiss: 'Dismiss notice',
        },
      },
      topbar: {
        ariaLabel: 'AI interaction balance: {{value}}. Open Plans and usage.',
        pausedAriaLabel: 'Agent paused: no AI interactions left. Open Plans and usage.',
        paused: 'Paused',
      },
      dashboard: {
        title: 'AI interactions',
        available: '{{value}} available',
        consumedOf: '{{consumed}} of {{credits}} used',
        trialEndsAt: 'Trial until {{date}}',
        paused: 'Agent paused: no AI interactions left',
        viewUsage: 'View usage',
        viewPlans: 'View plans and usage',
        loadError: 'Could not load the balance.',
      },
      paused: {
        badge: 'Paused: no credits',
        title: 'The agent is paused: AI interactions ran out.',
        description: 'Messages keep arriving and your team can reply.',
        action: 'View plans and usage',
      },
      onboarding: {
        organization:
          'Your organization starts with a free trial of {{credits}} interactions for {{days}} days. No credit card.',
        goLive: 'When the trial ends, choose a plan in Plans and usage.',
      },
    },
    shell: {
      sidebar: {
        collapse: 'Collapse menu',
        expand: 'Expand menu',
        openMenu: 'Open navigation menu',
        closeMenu: 'Close navigation menu',
      },
      nav: {
        dashboard: 'Overview',
        desk: 'Support',
        pendencias: 'Pending',
        agents: 'Agents',
        knowledge: 'Knowledge',
        channels: 'Channels',
        webchat: 'WebChat',
        integrations: 'Integrations',
        analytics: 'Analytics',
        audit: 'Audit',
        team: 'Team',
        billing: 'Plans and usage',
        settings: 'Settings',
      },
      tenantSwitcher: {
        ariaLabel: 'Select organization',
        openLabel: 'Switch organization',
        current: 'Current organization',
      },
    },
    dashboard: {
      greeting: 'Hi, {{name}}',
      organization: {
        title: 'Your organization',
        nameLabel: 'Name',
        slugLabel: 'Identifier',
        statusLabel: 'Status',
        createdAtLabel: 'Created on',
        statuses: {
          Provisioning: 'Provisioning',
          Active: 'Active',
          Suspended: 'Suspended',
          Disabled: 'Disabled',
          Deleted: 'Deleted',
        },
      },
      activity: {
        title: 'Activity in the last {{days}} days',
        inbound: 'Conversations received',
        executions: 'Agent runs',
        autoReplies: 'Automatic replies',
        suggestions: 'Suggestions to the team',
        loadError: 'We could not load recent activity.',
      },
      members: {
        title: 'Team',
        countSingular: '{{count}} person on the team',
        countPlural: '{{count}} people on the team',
        viewAll: 'View team',
        loadError: 'We could not load the team.',
      },
      modules: {
        title: 'Explore your workspace',
        subtitle: 'Jump into the areas of your support.',
      },
      shortcuts: {
        desk: "See and reply to your customers' conversations.",
        agents: 'Configure how your agent answers and test it in the Playground.',
        knowledge: 'Teach your agent with your website and documents.',
        channels: 'Connect and track your support channels.',
        integrations: 'See what your agent can look up in your systems.',
        analytics: 'Track how your support is performing.',
      },
    },
    desk: {
      title: 'Support',
      agentMode: {
        label: 'Agent',
        loading: 'Loading...',
        observeOnly: 'Observe only',
        suggest: 'Suggesting',
        autoReply: 'Auto reply',
        none: 'No active agent',
      },
      filters: {
        label: 'Filter conversations',
        open: 'Open',
        pending: 'Waiting for a human',
        resolved: 'Resolved',
        all: 'All',
      },
      statuses: {
        open: 'Open',
        pending: 'Waiting for a human',
        resolved: 'Resolved',
      },
      list: {
        emptyTitle: 'No conversations here yet',
        emptyDescription:
          'When your customers message you on WhatsApp, their conversations show up here for you to reply.',
        reload: 'Refresh',
        unknownContact: 'Unnamed contact',
        loadFailed: 'Could not load conversations.',
      },
      detail: {
        noneSelectedTitle: 'Select a conversation',
        noneSelectedDescription: 'Pick a conversation from the list to see the messages and reply.',
        back: 'Back',
        loadFailed: 'Could not load the conversation.',
      },
      channelNotReady: {
        title: 'Your support channel is not ready yet',
        description: 'Connect WhatsApp to start receiving and replying to conversations here.',
        action: 'Connect channel',
      },
      header: {
        resolve: 'Resolve',
        reopen: 'Reopen',
        resolved: 'Conversation resolved',
        reopened: 'Conversation reopened',
      },
      triageCard: {
        title: 'Triage',
        notTriaged: 'This conversation has not been analyzed yet.',
        excluded: 'This conversation is excluded from triage.',
        exclude: 'Do not triage this conversation',
        includeBack: 'Triage this conversation again',
        important: 'Important contact',
        notImportant: 'Mark contact as important',
        updated: 'Preference updated',
        actionFailed: 'Could not update the preference.',
      },
      thread: {
        internalNote: 'Internal note',
        empty: 'No messages in this conversation yet.',
        authors: {
          agent: 'AI agent',
          operator: 'Team',
          operatorPhone: 'You, from your phone',
        },
        attachment: 'Attachment',
        transcript: 'Transcript',
        media: {
          open: 'Open the image in a new tab',
          imageAlt: 'Image sent in the conversation',
          download: 'Download',
          document: 'Document',
        },
      },
      agent: {
        panelTitle: 'AI agent',
        suggestionTitle: 'Agent suggestion',
        postedAsNote: 'Posted as note',
        notPosted: 'Not posted',
        autoReplied: 'Replied to customer',
        noText: 'The agent did not produce a reply text.',
        empty: 'The agent has not analyzed this conversation yet.',
        riskLevels: {
          none: 'None',
          low: 'Low',
          medium: 'Medium',
          high: 'High',
        },
        trace: {
          risk: 'Risk',
          model: 'Model',
          tokens: 'Tokens',
          duration: 'Duration',
          durationValue: '{{ms}} ms',
        },
      },
      composer: {
        modeLabel: 'Message type',
        reply: 'Reply',
        note: 'Internal note',
        noteHint: 'Visible only to your team. The customer does not receive this message.',
        sendFailed: 'Could not send.',
        replyPlaceholder: 'Write your reply to the customer...',
        notePlaceholder: 'Write a note for your team...',
        send: 'Send',
        saveNote: 'Save note',
        replySent: 'Reply sent',
        noteSaved: 'Internal note added',
      },
      ticket: {
        label: 'Ticket #{{number}}',
        statusWaiting: 'Waiting for an agent',
        statusInProgress: 'In progress',
        openedAgo: 'opened {{time}} ago',
        openedOffline: 'Opened outside service hours',
        listWaiting: '#{{number}} · waiting {{time}}',
        listInProgress: '#{{number}} · in progress',
        waitingNow: 'moments',
        waitingMinutes: '{{count}} min',
        waitingHours: '{{count}} h',
        waitingDays: '{{count}} d',
        reason: 'Reason',
        summary: 'AI summary',
        owner: 'Owner',
        ownerMe: 'You',
        ownerNobody: 'Nobody has taken it yet',
        aiPaused:
          'The AI is paused in this conversation until the ticket is handed back or the conversation is resolved.',
        takeOver: 'Take over',
        takeOverConversation: 'Take over conversation',
        returnToAgent: 'Hand back to AI',
        tookOver: 'You took over the conversation',
        returned: 'Conversation handed back to the AI',
        actionFailed: 'The action could not be completed.',
        reasons: {
          UserRequestedHuman: 'The customer asked to talk to a person',
          LowConfidence: 'The AI was not confident enough to answer',
          CriticalIssue: 'Critical issue',
          FinancialIssue: 'Financial matter',
          Complaint: 'Complaint',
          SensitiveRequest: 'Sensitive request',
          PolicyBlocked: 'Blocked by policy',
          MissingInformation: 'Missing information to solve it',
          AgentFailure: 'The AI could not complete the lookup',
          OperatorTakeover: 'An agent took over the conversation',
          other: 'Handed off to a human',
        },
      },
    },
    triage: {
      title: 'Pending',
      subtitle: 'What awaits your reply, what awaits the contact, and the open tasks.',
      loadFailed: 'Could not load what is pending.',
      disabled: {
        title: 'Triage is off',
        description:
          'Turn triage on to see here, by priority, who is waiting for your reply, a summary of each conversation and a draft ready to send.',
        action: 'Turn on in Settings',
      },
      disabledBanner: {
        title: 'Triage is off',
        description:
          'The conversations below await you, but without triage there is no summary, priority or draft.',
      },
      awaitingYou: {
        title: 'Awaiting you',
        emptyTitle: 'All caught up',
        emptyDescription: 'No conversation is waiting for your reply right now.',
      },
      awaitingThem: {
        title: 'Awaiting them',
        empty: 'No conversation is waiting for the contact.',
        repliedAgo: 'replied {{time}} ago',
      },
      card: {
        waitingFor: 'waiting for {{time}}',
        importantContact: 'Important contact',
        excluded: 'Excluded from triage',
        notTriaged: 'Not analyzed yet',
        noSummary: 'Triage has not analyzed this conversation yet.',
        need: 'Needs',
        draft: 'Suggested draft',
        editDraft: 'Edit draft',
        send: 'Send',
        edit: 'Edit',
        doneEditing: 'Done',
        dismiss: 'Dismiss',
        sent: 'Reply sent',
        sendFailed: 'Could not send the reply.',
        dismissed: 'Draft dismissed',
        actionFailed: 'Could not complete the action.',
        openConversation: 'Open conversation',
      },
      tasks: {
        title: 'Tasks',
        filterLabel: 'Filter tasks',
        filters: { open: 'Open', done: 'Done' },
        newTaskLabel: 'New task',
        newTaskPlaceholder: 'New task...',
        add: 'Add',
        created: 'Task created',
        complete: 'Complete',
        dismissTask: 'Dismiss',
        done: 'Done',
        suggestedByAi: 'suggested by AI',
        fromConversation: 'Conversation with {{contact}}',
        emptyTitle: 'No open tasks',
        emptyDescription:
          'Commitments and requests detected in conversations show up here. You can also add a task by hand.',
        emptyDoneTitle: 'No completed tasks',
        emptyDoneDescription: 'Tasks you complete stay here.',
        actionFailed: 'Could not complete the action.',
      },
    },
    presence: {
      openLabel: 'Change my availability',
      available: 'Available',
      away: 'Away',
      availableHint: 'You receive the conversations the AI hands off.',
      awayHint: 'Paused: the AI tells customers the team will reply later.',
      waiting: 'Waiting: {{count}}',
      newTicket: 'New ticket waiting for an agent (in queue: {{count}})',
      changeFailed: 'Your availability could not be changed.',
      team: {
        available: 'Team online now ({{count}})',
        outsideHours: 'Outside service hours — customers are answered {{when}}',
        nobodyOnline: 'Nobody available now — customers leave a message and a ticket is opened',
      },
    },
    analytics: {
      title: 'Analytics',
      subtitle: 'Real metrics for your support and agents, over the selected period.',
      loading: 'Loading metrics...',
      loadFailed: 'Could not load the metrics.',
      unavailable: '—',
      notAvailable: 'Not available yet',
      window: {
        label: 'Period',
        last7: 'Last 7 days',
        last30: 'Last 30 days',
        last90: 'Last 90 days',
      },
      export: {
        button: 'Export CSV',
        columns: {
          day: 'Day',
          executions: 'Executions',
          succeeded: 'Succeeded',
          failed: 'Failed',
          promptTokens: 'Input tokens',
          completionTokens: 'Output tokens',
        },
      },
      units: {
        ms: '{{value}} ms',
        percent: '{{value}}%',
      },
      empty: {
        title: 'No metrics to show yet',
        description:
          'As soon as your support starts receiving and analyzing conversations, the real numbers show up here.',
      },
      agent: {
        section: 'Agent activity',
        total: 'Agent analyses',
        succeeded: 'Completed',
        failed: 'Failures',
        successRate: 'Success rate',
        suggestions: 'Suggestions to operator',
        suggestionsHint: 'Times the agent escalated to a human with a private suggestion.',
        autoReplies: 'Automatic replies',
        autoRepliesHint:
          'Times the agent replied to the customer on its own (automatic reply mode).',
        avgLatency: 'Average latency',
        p95Latency: 'p95 latency',
        byMode: 'By mode',
        byModel: 'By model',
        daily: 'Analyses per day',
        dailyAria: 'Agent runs per day, stacked by completed and failed.',
        emptyTitle: 'The agent has not analyzed any conversation yet',
        emptyDescription: 'Once messages arrive, the agent analyses appear here.',
        modes: {
          ObserveOnly: 'Observe only',
          Suggest: 'Suggesting',
          AutoReply: 'Auto reply',
        },
      },
      handoffs: {
        title: 'Handoffs to a human',
        description:
          'A full handoff funnel is not available yet. Meanwhile, we show the real escalation signal: suggestions sent to the operator.',
      },
      inbound: {
        section: 'Incoming messages',
        total: 'Messages received',
        avgProcessing: 'Average processing time',
        byStatus: 'By status',
        failures: 'Processing failures',
        noFailures: 'No failures in the period.',
        emptyTitle: 'No messages received in the period',
        emptyDescription: 'Your customers’ messages appear here as soon as they arrive.',
        statuses: {
          Pending: 'Queued',
          Processed: 'Processed',
          Skipped: 'Skipped',
          Failed: 'Failed',
        },
      },
      knowledge: {
        section: 'Knowledge',
        documents: 'Documents',
        ready: 'Ready',
        processing: 'Processing',
        pending: 'Queued',
        failed: 'Failed',
        chunks: 'Indexed passages',
        emptyTitle: 'No documents in the knowledge base',
        emptyDescription: 'Add documents so the agent can answer accurately.',
        addAction: 'Add documents',
      },
      channels: {
        section: 'Channels',
        emptyTitle: 'No channel connected',
        emptyDescription: 'Connect WhatsApp to monitor your channel health here.',
        connectAction: 'Connect channel',
        statuses: {
          Created: 'Created',
          WaitingForPairing: 'Waiting to connect',
          Connected: 'Connected',
          Disconnected: 'Disconnected',
          Failed: 'Failed',
        },
        types: {
          WhatsApp: 'WhatsApp',
          WebChat: 'WebChat',
          Email: 'Email',
          Instagram: 'Instagram',
          Telegram: 'Telegram',
          Teams: 'Teams',
          Api: 'API',
        },
      },
      usage: {
        section: 'Usage',
        agents: 'Agents',
        channels: 'Channels',
        users: 'People',
        documents: 'Documents',
        aiExecutions: 'AI analyses',
        promptTokens: 'Input tokens',
        completionTokens: 'Output tokens',
        totalTokens: 'Total tokens',
        tokensNotAvailable: 'Tokens not recorded yet',
      },
    },
    audit: {
      title: 'Audit',
      subtitle: 'Who did what, when and on which resource.',
      loading: 'Loading audit...',
      loadFailed: 'Could not load the audit trail.',
      empty: {
        title: 'No audit records',
        description: 'Your team’s actions appear here as soon as they happen.',
        filteredTitle: 'No results for these filters',
        filteredDescription: 'Adjust the date range or actor type to see more records.',
      },
      filters: {
        actorType: 'Actor type',
        all: 'All',
        clear: 'Clear filters',
        from: 'From',
        to: 'To',
        correlationId: 'Correlation',
        correlationIdPlaceholder: 'Search by ID...',
        invalidRange: 'The start date cannot be after the end date.',
      },
      export: {
        button: 'Export CSV',
        truncated: 'We exported the first {{max}} records. Refine the filters to export fewer.',
        failed: 'Could not export the audit trail.',
      },
      actorTypes: {
        User: 'Person',
        System: 'System',
        Agent: 'Agent',
        PlatformAdministrator: 'Platform administrator',
      },
      table: {
        timestamp: 'Date and time',
        actor: 'Actor',
        action: 'Action',
        resource: 'Resource',
        correlation: 'Correlation',
        viewDetail: 'View details',
      },
      actions: {
        tenant: {
          created: 'Organization created',
        },
        conversation: {
          replied: 'Reply sent to customer',
          note_added: 'Internal note added',
          resolved: 'Conversation resolved',
          reopened: 'Conversation reopened',
        },
      },
      resources: {
        Tenant: 'Organization',
        Conversation: 'Conversation',
      },
      detail: {
        title: 'Record detail',
        occurredAt: 'Date and time',
        actor: 'Actor',
        action: 'Action',
        resource: 'Resource',
        correlation: 'Correlation',
        metadata: 'Metadata',
        noMetadata: 'No metadata for this record.',
        close: 'Close',
      },
      pagination: {
        summary: '{{from}}–{{to}} of {{total}}',
        prev: 'Previous',
        next: 'Next',
      },
      unknownActor: 'System',
    },
    modules: {
      desk: {
        title: 'Support is not available yet',
        description:
          "Here you'll see and reply to your customers' conversations in one place. This area arrives in an upcoming setup step.",
      },
      pendencias: {
        title: 'Pending',
        description: 'Select an organization to see what is pending.',
      },
      agents: {
        title: 'Agents are not available yet',
        description:
          "Here you'll create and configure the AI agents that serve your customers. This area arrives in an upcoming setup step.",
      },
      knowledge: {
        title: 'Knowledge is not available yet',
        description:
          "Here you'll add websites, PDFs and FAQs for your agent to answer accurately. This area arrives in an upcoming setup step.",
      },
      channels: {
        title: 'No channel connected',
        description: 'Connect your WhatsApp — available in the next setup step.',
      },
      integrations: {
        title: 'Integrations are not available yet',
        description:
          "Here you'll connect your product to other tools in your business. This area arrives in an upcoming setup step.",
      },
      analytics: {
        title: 'Analytics is not available yet',
        description:
          "Here you'll track support metrics and agent performance. This area arrives in an upcoming setup step.",
      },
      settings: {
        title: 'Settings are not available yet',
        description:
          "Here you'll manage your organization's data and preferences. This area arrives in an upcoming setup step.",
      },
    },
    team: {
      title: 'Team',
      subtitle: 'People with access to this organization.',
      you: '(you)',
      roleLabel: 'Role',
      statusLabel: 'Status',
      memberLabel: 'Member',
      membersCaption: 'Team members',
      actionsLabel: 'Actions',
      remove: 'Remove',
      actionFailed: 'We could not complete the action.',
      removeConfirm: {
        title: 'Remove member?',
        description:
          '{{name}} will lose access to this organization. You can invite them again later.',
        confirm: 'Remove',
        cancel: 'Cancel',
      },
      toast: {
        roleChanged: 'Role updated',
        removed: '{{name}} was removed from the team',
        invited: 'Invitation sent to {{email}}',
        invitationRevoked: 'Invitation revoked',
      },
      invite: {
        title: 'Invite by email',
        subtitle: 'The person gets a link to join with the chosen role.',
        emailLabel: 'Email',
        emailPlaceholder: 'person@company.com',
        submit: 'Send invitation',
        submitting: 'Sending...',
      },
      pending: {
        title: 'Pending invitations',
        expiresAt: 'expires on {{date}}',
        expired: 'Expired',
        revoke: 'Revoke',
      },
      link: {
        title: 'Invitation link',
        subtitle: 'We emailed it to {{email}}. You can also copy the link and share it.',
        note: 'The link is shown once. If needed, create a new invitation.',
        linkLabel: 'Invitation link',
        copy: 'Copy',
        copied: 'Copied!',
        copyFailed: 'Failed — select and copy',
        done: 'Done',
      },
      noPermission: {
        title: "You don't have access to the team",
        description: 'Ask an administrator of your organization to manage who is part of the team.',
      },
      roles: {
        Owner: 'Owner',
        Administrator: 'Administrator',
        Supervisor: 'Supervisor',
        Agent: 'Agent',
        Analyst: 'Analyst',
      },
      statuses: {
        Invited: 'Invited',
        Active: 'Active',
        Suspended: 'Suspended',
        Revoked: 'Revoked',
      },
    },
    acceptInvite: {
      working: 'Processing your invitation...',
      success: 'Invitation accepted. Welcome to the team!',
      alreadyMember: 'You are already part of this organization.',
      errorTitle: 'We could not accept the invitation',
      genericError: 'The invitation is invalid or has expired.',
      backHome: 'Back to home',
    },
    settings: {
      pageTitle: 'Settings',
      pageSubtitle: 'Manage your organization details and interface preferences.',
      triage: {
        title: 'Triage',
        description:
          'An assistant that reads your WhatsApp conversations, says whose turn it is, summarizes, prioritizes and drafts a reply — you decide what to send. Nothing is sent without your action.',
        loadFailed: 'Could not load the triage settings.',
        saveFailed: 'Could not save the triage settings.',
        save: 'Save',
        saved: 'Settings saved',
        general: {
          title: 'Pending assistant',
          enable: 'Turn triage on',
          enableHint:
            'Analyzes conversations once they go quiet and shows what is pending in Pending.',
          transcribe: 'Transcribe audio',
          transcribeHint: 'Incoming voice messages become text for triage and for support.',
          quietWindow: 'Quiet window (minutes)',
          quietWindowHint:
            'Waits for the conversation to go quiet before analyzing (1 to 30 minutes).',
        },
        digest: {
          title: 'Daily summary',
          enable: 'Receive a daily summary on WhatsApp',
          enableHint:
            'One message a day, at the chosen time, with what awaits you and the open tasks.',
          time: 'Time',
          timeZone: 'Time zone',
          recipient: 'Recipient number',
          recipientPlaceholder: 'Digits only, with country and area codes',
          recipientHint: 'Leave empty to use the channel number.',
          sendNow: 'Send summary now',
          sendNowHint:
            "Sends today's summary right away, on WhatsApp, so you can check it. The scheduled one is unaffected.",
          sendNowDirty: 'Save your changes before sending the summary.',
          sendNowSent: 'Summary sent',
          sendNowFailed: 'The summary could not be sent.',
          lastSent: 'Last summary sent on {{date}}.',
          lastError: {
            title: 'The last summary did not go out',
            recipientUnavailable:
              'No recipient number: enter a number above or connect the WhatsApp channel.',
            channelUnavailable: 'No connected WhatsApp channel to send the summary.',
            sendFailed: 'The channel could not send the summary. We will retry on the next cycle.',
            unknown: 'The summary could not be sent. We will retry on the next cycle.',
          },
        },
      },
      handoff: {
        title: 'Human support',
        description:
          'When the AI hands off to your team: your service hours, what the customer reads and who is notified. Outside hours or with nobody available, the customer leaves a message and a ticket waits in the queue.',
        loadFailed: 'The support settings could not be loaded.',
        teamNow: 'Now:',
        hours: {
          title: 'Service hours',
          enable: 'Use service hours',
          enabledHint:
            'Outside these hours the customer is told when the team is back, even if someone is online.',
          disabledHint: 'No hours set: the team answers whenever someone is Available.',
          timeZone: 'Time zone',
          until: 'to',
          opens: '{{day}} opening time',
          closes: '{{day}} closing time',
          closed: 'Closed',
          addWindow: 'Add interval',
          removeWindow: 'Remove interval',
          noWindows: 'Select at least one service day.',
          inverted: 'Each interval must end after it starts.',
          overlap: 'Intervals on the same day cannot overlap.',
        },
        messages: {
          title: 'Messages to the customer on handoff',
          hint: 'Leave blank to use the default text. Use {protocolo} for the ticket number and {retorno} for when the team is back (e.g. "amanhã a partir das 08:00").',
          available: 'With the team available',
          unavailable: 'Nobody available or outside hours',
        },
        notify: {
          label: 'Notify the team by email',
          hint: 'When a ticket is opened with nobody available, everyone who answers conversations gets an email with the link.',
        },
        save: 'Save',
        saved: 'Support settings saved',
        saveFailed: 'The settings could not be saved.',
        days: {
          monday: 'Monday',
          tuesday: 'Tuesday',
          wednesday: 'Wednesday',
          thursday: 'Thursday',
          friday: 'Friday',
          saturday: 'Saturday',
          sunday: 'Sunday',
        },
        timeZones: {
          America_Sao_Paulo: 'Brasília (São Paulo, Rio, South, GO, DF)',
          America_Bahia: 'Bahia',
          America_Fortaleza: 'Fortaleza (CE, RN, PB, MA, PI)',
          America_Recife: 'Recife (PE)',
          America_Belem: 'Belém (PA, AP)',
          America_Manaus: 'Manaus (AM)',
          America_Cuiaba: 'Cuiabá (MT)',
          America_Campo_Grande: 'Campo Grande (MS)',
          America_Porto_Velho: 'Porto Velho (RO)',
          America_Boa_Vista: 'Boa Vista (RR)',
          America_Rio_Branco: 'Rio Branco (AC)',
          America_Noronha: 'Fernando de Noronha',
        },
      },
      organization: {
        title: 'Organization',
        description: 'Your organization details. The name and identifier are set by the team.',
        nameLabel: 'Name',
        slugLabel: 'Identifier',
        statusLabel: 'Status',
        statuses: {
          Provisioning: 'Provisioning',
          Active: 'Active',
          Suspended: 'Suspended',
          Disabled: 'Disabled',
          Deleted: 'Deleted',
        },
        website: {
          label: 'Company website',
          hint: 'Your agent uses the website to learn about your business.',
          placeholder: 'mycompany.com',
          empty: 'No website added yet.',
          save: 'Save website',
          saved: 'Website updated.',
          invalid: 'Enter a valid website (e.g. mycompany.com).',
          error: 'The website could not be saved. Please try again.',
        },
      },
      language: {
        title: 'Interface language',
        description: 'Choose the language the product is shown in for you.',
        groupLabel: 'Interface language',
        current: 'Current',
        options: {
          ptBR: 'Português (Brasil)',
          enUS: 'English',
        },
      },
      account: {
        title: 'Account',
        description: 'The account you are signed in with.',
        nameLabel: 'Name',
        emailLabel: 'Email',
      },
      security: {
        title: 'Security',
        description: 'Change your account password.',
        currentLabel: 'Current password',
        newLabel: 'New password',
        newHint: 'At least 12 characters, with uppercase, lowercase and a number.',
        confirmLabel: 'Confirm new password',
        submit: 'Change password',
        success: 'Password changed successfully.',
        errors: {
          currentRequired: 'Enter your current password.',
          newLength: 'The new password must be at least 12 characters.',
          mismatch: 'The passwords do not match.',
        },
      },
      dangerZone: {
        title: 'Danger zone',
        description:
          'Deleting the organization is irreversible: it removes access for the whole team and turns off support.',
        deleteButton: 'Delete organization',
        confirmTitle: 'Delete this organization?',
        confirmDescription:
          'This action cannot be undone. The team loses access and support is turned off.',
        confirmPrompt: 'Type {{slug}} to confirm',
        confirmButton: 'Delete permanently',
      },
    },
    landing: {
      nav: {
        produto: 'Product',
        como: 'How it works',
        controle: 'Control',
        planos: 'Pricing',
        duvidas: 'FAQ',
        login: 'Sign in',
        signup: 'Create account',
      },
      brand: {
        mark: 'C',
        productTag: 'A Codout Product · SaaS',
      },
      hero: {
        badge: 'AI trained on your company knowledge',
        titleLine1: 'Your support replies',
        titleHighlight: 'in seconds, with precision',
        subtitle:
          'Connect WhatsApp, point it to your website, and the agent learns on its own. It suggests the reply with the source alongside it, and your team approves with one click — until you decide to let it reply directly.',
        ctaPrimary: 'Create my agent',
        ctaSecondary: 'See how it works',
        trustNoCard: 'No credit card',
        trustFirstAgent: 'First agent in minutes',
        trustIsolatedData: 'Data isolated per company',
        industriesLabel: 'Built for teams that support customers on WhatsApp',
        industry1: 'E-commerce',
        industry2: 'Recurring services',
        industry3: 'B2B software',
        industry4: 'Health and clinics',
        industry5: 'Education',
        webchat: {
          siteTag: 'customer website',
          agentInitial: 'AU',
          agentName: 'Aurora',
          agentStatus: 'online · replies instantly',
          greetingHello: 'Hi,',
          customerName: 'Clovis',
          greetingSeen: "! I see you're on the",
          planName: 'Pro',
          greetingHelp: 'plan. How can I help today?',
          customerAsk: 'has my order shipped yet?',
          answerPrefix: 'Yes! Your order',
          orderNumber: '#4821',
          answerSuffix: 'shipped today at 9:14 AM and arrives by 6 PM.',
          sourceLabel: 'checked live ·',
          sourceTool: 'orders',
          quickReply1: 'Track delivery',
          quickReply2: 'View my orders',
          quickReply3: 'Talk to an agent',
          inputPlaceholder: 'Write your message…',
          poweredBy: 'Powered by',
          handoffTitle: 'Handed off to the team',
          handoffDesc: 'History and reason already with a human agent.',
          handoffEtaLabel: 'avg. reply',
          handoffEta: '2 min',
        },
      },
      product: {
        eyebrow: 'What changes in your operation',
        title: 'Less waiting for the customer, less repeated work for the team',
        subtitle:
          "The agent handles what's repetitive and always shows where the information came from. What needs a person still gets a person, with the context ready.",
        stat1Value: '1st',
        stat1Title: 'Reply within the minute',
        stat1Desc:
          'The suggestion is ready before anyone opens the conversation. Approving takes one click.',
        stat2Value: '100%',
        stat2Title: 'Traceable',
        stat2Desc:
          'Every reply cites the document, excerpt and query used. Every change is audited.',
        stat3Value: '0',
        stat3Title: 'Automation without notice',
        stat3Desc:
          'Automatic replies only exist when you turn them on, and the current mode shows on every screen.',
        stat4Value: '1',
        stat4Title: 'Place to operate',
        stat4Desc: 'Conversations, knowledge, integrations and team in the same panel.',
      },
      howItWorks: {
        eyebrow: 'How it works',
        title: 'From sign-up to the first conversation',
        subtitle: 'Four steps, none of them technical.',
        step1Number: '01',
        step1Title: 'Tell us about your company',
        step1Desc: 'Name, website and segment. Less than a minute.',
        step2Number: '02',
        step2Title: 'The agent learns',
        step2Desc:
          'We read the public pages, identify products, and organize the frequently asked questions.',
        step3Number: '03',
        step3Title: 'You test it first',
        step3Desc: 'Chat with it in the Playground and adjust the tone before any customer does.',
        step4Number: '04',
        step4Title: 'Connect WhatsApp',
        step4Desc:
          'Scan the QR code and start supporting customers on the channel they already use.',
      },
      featureSuggestion: {
        eyebrow: 'Suggestion with source',
        title: 'The team sees the reply and where it came from',
        desc: 'No more replies nobody can explain. The panel shows the document, the excerpt and the query made in your system, with the time for each step.',
        bullet1: 'Approve, edit or discard in one click',
        bullet2: "Internal notes kept separate from the customer's conversation",
        bullet3: 'Customer history and context on the same screen',
        mockLabel: 'Agent suggestion',
        mockText:
          'A size exchange can be requested up to 30 days after delivery, with the original tag. Want me to open the request?',
        sourceCardTitle: 'Exchange policy · excerpt 4',
        sourceCardQuote: '"An exchange can be requested through the website within 30 days…"',
        toolBadge: 'READ',
        toolCode: 'get_order(48213)',
        toolLatency: '✓ 320 ms',
      },
      featureKnowledge: {
        eyebrow: 'Living knowledge',
        title: 'It flags when information is missing',
        desc: 'Website, PDFs, FAQs and pasted text become the knowledge base. When a customer asks something nobody documented, the topic shows up as a gap — with how many times it was asked.',
        link: 'See the knowledge screen →',
        row1Label: 'Products',
        row1Status: 'COMPLETE',
        row2Label: 'Shipping and delivery',
        row2Status: 'COMPLETE',
        row3Label: 'Exchanges',
        row3Status: 'PARTIAL',
        row4Label: 'Cancellation',
        row4Meta: 'asked 9× with no answer in the base',
        row4Action: 'Fill in',
      },
      featureActions: {
        eyebrow: 'Actions on your systems',
        title: 'Real queries, without opening the gate',
        desc: 'Import your API specification and choose what the agent is allowed to do. Every operation gets a risk level: read is enabled, write asks for confirmation, destructive comes turned off.',
        link: 'See the integration assistant →',
        tool1Badge: 'READ',
        tool1Code: 'get_order',
        tool1Status: 'enabled',
        tool2Badge: 'WRITE',
        tool2Code: 'update_address',
        tool2Status: 'with confirmation',
        tool3Badge: 'DESTRUCTIVE',
        tool3Code: 'cancel_contract',
        tool3Status: 'disabled',
      },
      control: {
        eyebrow: 'Control and trust',
        title: 'You decide how much the AI can do',
        subtitle:
          'Three automation levels, switched whenever you want. The current level stays visible on every critical screen and every change is audited.',
        level1Badge: 'Observing',
        level1Title: 'Just watches',
        level1Desc: 'Ideal for the first week: it learns from conversations without stepping in.',
        level2Badge: 'Suggesting · recommended',
        level2Title: 'Prepares, you approve',
        level2Desc: 'The team gains speed without losing the final word.',
        level3Badge: 'Automatic reply',
        level3Title: 'Replies to what it knows well',
        level3Desc: 'Requires confirmation to turn on and is never enabled by default.',
        guarantee1Title: 'Handoff with context',
        guarantee1Desc:
          'Sensitive cases go to the team with the reason, the history and the AI paused.',
        guarantee2Title: 'Roles per person',
        guarantee2Desc: 'Owner, supervisor, agent and analyst, each with what they need.',
        guarantee3Title: 'Full audit trail',
        guarantee3Desc: 'Who did it, what changed, when and on which resource.',
        guarantee4Title: 'Isolated data',
        guarantee4Desc: 'Each company with its own separated data and encrypted credentials.',
      },
      channels: {
        eyebrow: 'Channels and integrations',
        title: 'Available today, and what comes next',
        card1Title: 'WhatsApp',
        card1Desc: 'QR code connection, ready to use.',
        card1Status: 'AVAILABLE',
        card2Title: 'Own API',
        card2Desc: 'Import your specification and choose the operations.',
        card2Status: 'AVAILABLE',
        card3Title: 'Official WhatsApp Business',
        card3Desc: 'Verified account and templates.',
        card3Status: 'COMING SOON',
        card4Title: 'WebChat, email, Instagram',
        card4Desc: 'Same agents, new channels.',
        card4Status: 'PLANNED',
      },
      pricing: {
        title: 'Pricing',
        badge: 'PROVISIONAL PRICING',
        subtitle: 'Prices still being defined. The limits below already reflect the product.',
        subtitlePriced:
          'Fixed monthly price, no lock-in. Start with the free trial and subscribe whenever you want.',
        priceUnset: '—',
        pricePeriod: '/mo',
        starterName: 'Starter',
        starterDesc: 'To start supporting customers with AI',
        starterFeature1: '1 WhatsApp channel',
        starterFeature2: '1 agent',
        starterFeature3: 'Basic knowledge base',
        starterFeature4: 'Limited AI quota',
        starterCta: 'Get started',
        professionalName: 'Professional',
        professionalBadge: 'MOST CHOSEN',
        professionalDesc: 'For a complete support operation',
        professionalFeature1: 'Multiple channels and agents',
        professionalFeature2: 'Tools and integrations',
        professionalFeature3: 'Analytics and audit trail',
        professionalFeature4: 'Expanded AI quota',
        professionalCta: 'Create account',
        enterpriseName: 'Enterprise',
        enterpriseDesc: 'For corporate requirements',
        enterprisePrice: 'Contact us',
        enterpriseFeature1: 'SSO and advanced audit trail',
        enterpriseFeature2: 'SLA and tailored tools',
        enterpriseFeature3: 'Official WhatsApp',
        enterpriseFeature4: 'Dedicated deployment',
        enterpriseCta: 'Talk to sales',
      },
      faq: {
        title: 'Questions before you start',
        q1: 'Will the AI reply incorrectly to my customer?',
        a1: 'In the recommended mode, no message is sent without someone approving it. And every suggestion comes with its source, so you can check it in seconds.',
        q2: 'Do I need someone technical to set it up?',
        a2: 'No. Sign-up asks for name, website and segment; the rest is suggested. Connecting WhatsApp means scanning a QR code. Integrating your API is optional and guided step by step.',
        q3: "What happens when it doesn't know the answer?",
        a3: 'It hands off to the team with the reason and the history, and logs the topic as a knowledge gap for you to fill in later.',
        q4: 'Is my data mixed with other companies’ data?',
        a4: 'No. Each company has its data isolated, with role-based permissions and encrypted integration credentials.',
        q5: 'Can I use my current phone number?',
        a5: 'We recommend a number dedicated to support, to keep personal and sales traffic separate. The connection is made from the device that serves your customers.',
      },
      finalCta: {
        title: 'Put your agent to work today',
        subtitle:
          'Create the account, point it to your website, and test the agent before connecting any channel. No card, no consultant.',
        subtitleTrial:
          'Free trial with {{credits}} interactions for {{days}} days. No card, no consultant.',
        ctaPrimary: 'Create free account',
        ctaSecondary: 'See the product up close',
      },
      footer: {
        copyright: '© {{year}} {{productName}}',
        security: 'Security',
        allScreens: 'All screens',
      },
    },
  },
}
